import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  inject,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  AccessManagementUserCollectionRecord,
  AccessManagementUserCollectionUserRecord,
  UserRecord,
} from '@cdw-ae/common-data';
import { CdwButtonModule } from '@cdw-ae/common-ui-components-button';
import { CdwConfirmationDialogComponent } from '@cdw-ae/common-ui-components-dialog';
import { CdwDividerModule } from '@cdw-ae/common-ui-components-divider';
import {
  CdwColDef,
  CdwSearchGridComponent,
  CdwSearchGridModule,
  CdwSidebarDef,
  ICdwSearchGridDataService,
} from '@cdw-ae/common-ui-components-search-grid';
import {
  CommonUiComponentsSearchInputModule,
  type SearchItem,
} from '@cdw-ae/common-ui-components-search-input';
import { CommonUiComponentsTextInputModule } from '@cdw-ae/common-ui-components-text-input';
import {
  UserCellRendererComponent,
  UserHoverCardService,
  UserProfileCardComponent,
} from '@cdw-ae/common-ui-components-user-profile-card';
import { IGetSqlDataParams, SqlResponse } from '@cdw-ae/filter-bar-interfaces';
import { Observable, of, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'lib-cdw-user-collection-edit',
  templateUrl: './cdw-user-collection-edit.component.html',
  styleUrls: ['./cdw-user-collection-edit.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    CdwButtonModule,
    CdwDividerModule,
    CommonUiComponentsTextInputModule,
    CdwSearchGridModule,
    UserProfileCardComponent,
    CommonUiComponentsSearchInputModule,
  ],
})
export class CdwUserCollectionEditComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @ViewChild('userSearchGrid')
  userSearchGrid!: CdwSearchGridComponent<UserRecord>;

  @Input() userCollection!: AccessManagementUserCollectionRecord | undefined;

  readonly grey400Color = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-primary-gray-400')
    .trim();

  @Input() selectedUsers: AccessManagementUserCollectionUserRecord[] = [];

  @Output() cancelUserCollectionEdit = new EventEmitter();

  @Output() submitUserCollectionEdit = new EventEmitter();

  @Output() deleteUserCollection = new EventEmitter();

  @Output() openCsvUpload = new EventEmitter();

  @Input() users: UserRecord[] = [];

  public selectedUserIds: (string | number)[] = [];
  public selectedRowIds: (string | number)[] = [];
  private allUsers: UserRecord[] = [];
  public usersToAdd: UserRecord[] = [];
  public availableUsers: SearchItem[] = [];

  userCollectionForm!: FormGroup;

  public columnDefs: CdwColDef[] = [
    {
      field: 'name',
      headerName: 'Users',
      cellRenderer: UserCellRendererComponent,
      flex: 1,
    },
  ];

  public userSearchGridDataService: ICdwSearchGridDataService<UserRecord>;
  public componentId = 'userCollectionEditGrid';

  // Disable sidebar controls - not needed for simple user list
  public sidebarDef: CdwSidebarDef = { toolPanels: [] };

  // Disable column menu and resizing - read-only user list
  public customColDefDefaults: CdwColDef = {
    resizable: false,
    suppressMenu: true,
    sortable: false,
    suppressMovable: true,
  };

  // Hover card service
  public hoverCardService = this.userHoverCardService;
  public cardData$ = this.userHoverCardService.cardData$;
  public cardPosition$ = this.userHoverCardService.position$;

  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private userHoverCardService: UserHoverCardService
  ) {
    // Initialize the data service to display users to add
    this.userSearchGridDataService = {
      fetchSqlData: (
        params: IGetSqlDataParams
      ): Observable<SqlResponse<UserRecord>> => {
        return of({
          data: [...this.usersToAdd],
          itemTotal: this.usersToAdd.length,
        });
      },
    };
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update allUsers when the users input changes
    // biome-ignore lint/complexity/useLiteralKeys: TypeScript requires bracket notation for SimpleChanges index signature
    if (!changes['users']?.currentValue) {
      return;
    }

    this.allUsers = [...this.users];
    this.updateAvailableUsers();
    // Trigger grid refresh if grid is already initialized
    if (this.userSearchGrid) {
      this.userSearchGrid.search();
    }
  }

  ngOnInit(): void {
    // Store users for filtering
    this.allUsers = [...this.users];

    if (this.userCollection) {
      this.userCollectionForm = this.formBuilder.group({
        name: [this.userCollection.name, Validators.required],
        description: [this.userCollection.description, Validators.required],
      });

      // Set initially selected user IDs from selectedUsers
      this.selectedUserIds = this.selectedUsers.map(
        (userCollectionUser) => userCollectionUser.userId
      );

      // Initialize usersToAdd with users that match selectedUserIds
      this.usersToAdd = this.allUsers.filter((user) =>
        this.selectedUserIds.includes(user.id)
      );
    } else {
      this.userCollectionForm = this.formBuilder.group({
        name: ['', Validators.required],
        description: ['', Validators.required],
      });
    }

    // Initialize available users for search
    this.updateAvailableUsers();
  }

  private updateAvailableUsers(): void {
    // Filter out users already in usersToAdd
    this.availableUsers = this.allUsers
      .filter((user) => !this.usersToAdd.some((u) => u.id === user.id))
      .map((user) => ({
        id: user.id,
        name: user.name,
        email: user.email,
      }));
  }

  ngAfterViewInit(): void {
    // Refresh grid to display initial usersToAdd
    if (this.userSearchGrid) {
      this.userSearchGrid.search();
    }
  }

  public onSelectedIdsChanged(selectedIds: (string | number)[]): void {
    // Track which rows are currently selected in the grid
    this.selectedRowIds = selectedIds;
  }

  public onUserSelected(item: SearchItem): void {
    // Find the full user record
    const selectedUser = this.allUsers.find((user) => user.id === item.id);
    if (!selectedUser) return;

    // Add user to usersToAdd
    this.usersToAdd = [...this.usersToAdd, selectedUser];

    // Update selectedUserIds
    this.selectedUserIds = this.usersToAdd.map((user) => user.id);

    // Mark form as dirty to trigger unsaved changes detection
    this.userCollectionForm.markAsDirty();

    // Update available users
    this.updateAvailableUsers();

    // Refresh grid to show new user
    if (this.userSearchGrid) {
      this.userSearchGrid.search();
    }
  }

  public removeSelectedUsers(): void {
    if (this.selectedRowIds.length === 0) {
      return;
    }

    // Remove selected users from usersToAdd array
    this.usersToAdd = this.usersToAdd.filter(
      (user) => !this.selectedRowIds.includes(user.id)
    );

    // Update selectedUserIds to match usersToAdd
    this.selectedUserIds = this.usersToAdd.map((user) => user.id);

    // Mark form as dirty to trigger unsaved changes detection
    this.userCollectionForm.markAsDirty();

    // Clear selectedRowIds
    this.selectedRowIds = [];

    // Update available users
    this.updateAvailableUsers();

    // Refresh grid to reflect changes
    if (this.userSearchGrid) {
      this.userSearchGrid.search();
    }
  }

  public onDeleteUserCollection() {
    this.deleteUserCollection.emit();
  }

  public onSaveUserCollection() {
    if (this.userCollectionForm?.controls) {
      const userCollectionData: {
        name: string;
        description: string;
        users: number[];
      } = {
        name: this.userCollectionForm.get('name')?.value as string,
        description: this.userCollectionForm.get('description')
          ?.value as string,
        users: this.usersToAdd.map((user) => Number(user.id)),
      };
      this.submitUserCollectionEdit.emit(userCollectionData);
    }
  }

  public onCancelUserCollection() {
    this.cancelUserCollectionEdit.emit();
  }

  public onOpenCsvUpload() {
    this.openCsvUpload.emit();
  }

  public get isSaveDisabled(): boolean {
    return !this.userCollectionForm.valid || this.usersToAdd.length === 0;
  }

  public pasteEmails(): void {
    const dialogRef = this.dialog.open(CdwConfirmationDialogComponent, {
      width: '500px',
      position: { bottom: '0px' }, // Position at bottom-center
      data: {
        title: 'Paste a list of users to add',
        message: 'Paste email addresses here - each email on a new line',
        type: 'info',
        confirmText: 'Add to list',
        cancelText: 'Cancel',
        requireInputConfirmation: true,
        useTextarea: true,
        textareaRows: 12,
        textareaPlaceholder: 'email@example.com\nanother@example.com',
        hideIcon: true,
        hideActionsBorder: true,
        // Custom styling for title
        titleBackgroundColor: 'var(--color-primary-white)',
        titleColor: 'var(--color-primary-gray-300)',
        titleFontFamily: 'Source Sans 3',
        titleFontSize: '22px',
        titleFontWeight: 700,
        // Custom styling for message
        messageColor: 'var--color-primary-gray-400)',
        fontFamily: 'Roboto',
        messageFontSize: '14px',
        messageFontWeight: 400,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result: { confirmed: boolean; inputValue?: string }) => {
        if (result?.confirmed && result.inputValue) {
          const emails = result.inputValue
            .split('\n')
            .map((email) => email.trim())
            .filter((email) => email.length > 0);

          if (emails.length > 0) {
            this.addUsersByEmails(emails);
          }
        }
      });
  }

  public addUsersByEmails(emails: string[]): void {
    // Get unique emails only (case-insensitive)
    const uniqueEmailsLowerCase = Array.from(
      new Set(emails.map((e) => e.toLowerCase()))
    );

    const matchedUsers: UserRecord[] = [];
    const unmatchedEmails: string[] = [];

    for (const email of uniqueEmailsLowerCase) {
      const user = this.allUsers.find((u) => u.email.toLowerCase() === email);
      if (user) {
        matchedUsers.push(user);
      } else {
        unmatchedEmails.push(email);
      }
    }

    // Add matched users to usersToAdd (avoid duplicates)
    const newUsers = matchedUsers.filter(
      (user) => !this.usersToAdd.some((u) => u.id === user.id)
    );
    const duplicateCount = matchedUsers.length - newUsers.length;

    if (newUsers.length > 0) {
      this.usersToAdd = [...this.usersToAdd, ...newUsers];

      // Update selectedUserIds to match usersToAdd
      this.selectedUserIds = this.usersToAdd.map((user) => user.id);

      // Mark form as dirty to trigger unsaved changes detection
      this.userCollectionForm.markAsDirty();

      // Update available users to exclude newly added users
      this.updateAvailableUsers();

      // Refresh grid to show new users
      if (this.userSearchGrid) {
        this.userSearchGrid.search();
      }
    }

    // Show success/warning message - only show count of users actually added
    if (newUsers.length > 0) {
      const messages: string[] = [];
      messages.push(
        `Added ${newUsers.length} user${newUsers.length === 1 ? '' : 's'}.`
      );
      if (unmatchedEmails.length > 0) {
        messages.push(
          `${unmatchedEmails.length} email${unmatchedEmails.length === 1 ? '' : 's'} not found.`
        );
      }
      this.snackBar.open(
        `${messages.join(' ')}\n\nPlease make sure to save the collection.`,
        'OK',
        {
          duration: 5000,
          panelClass: ['multiline-snackbar'],
        }
      );
    } else if (newUsers.length === 0 && duplicateCount > 0) {
      const message =
        duplicateCount === 1
          ? 'User is already in the list.'
          : `All ${duplicateCount} users are already in the list.`;
      this.snackBar.open(message, 'OK', {
        duration: 3000,
        panelClass: ['multiline-snackbar'],
      });
    } else if (matchedUsers.length === 0) {
      this.snackBar.open(
        'No matching users found for the provided emails.',
        'OK',
        { duration: 3000 }
      );
    }
  }
}
